import { GET_ONBOARDING_V2_VARIANT } from '@/components/constants/flags';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { markDemoBooked } from '@/utils/onboardingDemoState';
import { Checkbox } from '@heidi/design-system/checkbox';
import { Label } from '@heidi/design-system/label';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import { FieldType, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { ChevronDownIcon } from 'lucide-react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { trackFieldSelected } from '@/lib/analytics/scribe/ui';
import { track } from '@/lib/analytics/track';
import { useMeetingBooker } from './hooks/useMeetingBooker';
import { DemoDetails, MeetingCalendarPanel } from './meeting-booker-steps';
import {
  type MeetingBookerHandle,
  type MeetingBookerProps,
} from './meeting-booker.types';

export const MeetingBooker = forwardRef<MeetingBookerHandle, MeetingBookerProps>(
  function MeetingBooker(
    { meetingUrl, firstName = '', lastName = '', email = '', companyName },
    ref
  ) {
    const intl = useIntl();
    const [isOptedIn, setIsOptedIn] = useState(false);

    const {
      slug,
      timezone,
      activeDuration,
      selectedDate,
      selectedSlot,
      isCalendarOpen,
      isLoadingAvailability,
      availableDates,
      slotsForSelectedDate,
      clearSelection,
      setIsCalendarOpen,
      handleSelectDate,
      handleSelectSlot,
      handleSelectDuration,
      handleBook,
    } = useMeetingBooker(meetingUrl);

    useImperativeHandle(
      ref,
      () => ({
        getBookingIntent: () => {
          if (!isOptedIn || !selectedSlot || !slug || !email) return null;
          return {
            slug,
            duration: activeDuration,
            startTime: selectedSlot.startMillisUtc,
            firstName,
            lastName,
            email,
            timezone,
            ...(companyName ? { formFields: { company: companyName } } : {}),
          };
        },
        book: async () => {
          if (!isOptedIn || !selectedSlot) return false;

          const slotStartIso = new Date(selectedSlot.startMillisUtc).toISOString();
          track({
            event_name: TrackingEventName.MeetingBookingSubmitted,
            props: {
              source: 'onboarding_completion',
              // GRO-3157: variant-tag every booking so control and v3 funnels
              // can be compared. MeetingBooker is shared by control modal and
              // v3 LearnBasics/Completion steps, so we read PostHog at call
              // time rather than threading the variant through props.
              onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
            },
          });

          try {
            const formFields: Record<string, string> = {};
            if (companyName) {
              formFields.company = companyName;
            }
            await handleBook(
              firstName,
              lastName,
              email,
              Object.keys(formFields).length > 0 ? formFields : undefined
            );
            markDemoBooked();
            track({
              event_name: TrackingEventName.MeetingBooked,
              props: {
                source: 'onboarding_completion',
                slot_start_iso: slotStartIso,
              },
            });
            return true;
          } catch (err) {
            track({
              event_name: TrackingEventName.MeetingBookingFailed,
              props: {
                source: 'onboarding_completion',
                // Use error class name (e.g. 'Error', 'TypeError') — raw messages
                // from HubSpot can carry PHI (e.g. the registrant's email).
                error_type: err instanceof Error ? err.name : undefined,
              },
            });
            return false;
          }
        },
        hasSelection: !!selectedSlot,
        isOptedIn,
      }),
      [
        isOptedIn,
        selectedSlot,
        slug,
        activeDuration,
        timezone,
        companyName,
        firstName,
        lastName,
        email,
        handleBook,
      ]
    );

    // Event 5: Time Selection
    const handleSlotSelect = useCallback(
      (slot: typeof selectedSlot) => {
        if (slot) {
          handleSelectSlot(slot);
          trackFieldSelected({
            fieldName: 'book_demo_time',
            fieldType: FieldType.Button,
            screenName: ScreenName.OnboardingOrgSurvey,
          });
          track({
            event_name: TrackingEventName.MeetingSlotSelected,
            props: {
              source: 'onboarding_completion',
              // activeDuration is in milliseconds per HubSpot's meeting API.
              duration_minutes: Math.round(activeDuration / 60_000),
            },
          });
        }
      },
      [handleSelectSlot, activeDuration]
    );

    // Event 4: Date Selection
    const handleDateSelect = useCallback(
      (date: Date | undefined) => {
        handleSelectDate(date);
        if (date) {
          trackFieldSelected({
            fieldName: 'book_demo_date',
            fieldType: FieldType.Button,
            screenName: ScreenName.OnboardingOrgSurvey,
          });
        }
      },
      [handleSelectDate]
    );

    // Event 3: Duration Preference
    const handleDurationSelect = useCallback(
      (duration: number) => {
        if (duration === activeDuration) return;
        handleSelectDuration(duration);
        trackFieldSelected({
          fieldName: 'book_demo_duration',
          fieldType: FieldType.Toggle,
          screenName: ScreenName.OnboardingOrgSurvey,
        });
      },
      [activeDuration, handleSelectDuration]
    );

    // Event 1: Demo Opt-In
    const handleOptIn = useCallback(() => {
      setIsOptedIn(true);
      trackFieldSelected({
        fieldName: 'book_demo',
        fieldType: FieldType.Checkbox,
        screenName: ScreenName.OnboardingOrgSurvey,
      });
    }, []);

    // Event 2: Booking in Demo (popover opened)
    const handleCalendarOpenChange = useCallback(
      (open: boolean) => {
        setIsCalendarOpen(open);
        if (open) {
          trackFieldSelected({
            fieldName: 'book_demo_selection',
            fieldType: FieldType.Dropdown,
            screenName: ScreenName.OnboardingOrgSurvey,
          });
        }
      },
      [setIsCalendarOpen]
    );

    if (!isOptedIn) {
      return (
        <div className="flex w-full items-center gap-2">
          <Checkbox
            id="demo-booking-opt-in"
            checked={false}
            onCheckedChange={handleOptIn}
          />
          <Label htmlFor="demo-booking-opt-in">
            <FormattedMessage
              defaultMessage="Book a 1-1 demo with a Heidi expert"
              id="GXa6zYvcxG"
            />
          </Label>
        </div>
      );
    }

    const selectedTimeLabel = selectedSlot
      ? intl.formatTime(new Date(selectedSlot.startMillisUtc), {
          hour: 'numeric',
          minute: '2-digit',
          timeZone: timezone,
        })
      : undefined;

    return (
      <div className="flex w-full flex-col gap-3">
        {/* Checkbox */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="demo-booking-opt-in"
            checked={true}
            onCheckedChange={() => {
              setIsOptedIn(false);
              clearSelection();
            }}
          />
          <Label htmlFor="demo-booking-opt-in">
            <FormattedMessage
              defaultMessage="Book a 1-1 demo with a Heidi expert"
              id="GXa6zYvcxG"
            />
          </Label>
        </div>

        {/* Find a time label */}
        <TypographyP2Bold>
          <FormattedMessage defaultMessage="Find a time" id="O481j6s3Ay" />
        </TypographyP2Bold>

        {/* Select time popover */}
        <Popover open={isCalendarOpen} onOpenChange={handleCalendarOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex h-10 w-full items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none transition-all duration-300"
            >
              <span
                className={
                  selectedTimeLabel ? 'text-text-primary' : 'text-text-secondary'
                }
              >
                {selectedTimeLabel ?? (
                  <FormattedMessage defaultMessage="Select time" id="tzylNIZIrX" />
                )}
              </span>
              <ChevronDownIcon className="size-4 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            className="w-auto p-0"
            sideOffset={8}
          >
            <MeetingCalendarPanel
              isLoading={isLoadingAvailability}
              availableDates={availableDates}
              selectedDate={selectedDate}
              onSelectDate={handleDateSelect}
              activeDuration={activeDuration}
              onSelectDuration={handleDurationSelect}
              slots={slotsForSelectedDate}
              timezone={timezone}
              onSelectSlot={handleSlotSelect}
            />
          </PopoverContent>
        </Popover>

        {/* Demo details */}
        {selectedSlot && <DemoDetails selectedSlot={selectedSlot} timezone={timezone} />}
      </div>
    );
  }
);
